// ===== UNIFIED SIDEBAR + TOPBAR =====
// Single implementation replacing the four near-identical copies:
// sidebar(user).js, sidebar(admin).js, sidebar(Manager).js, sidebar(UseProfileManager).js
// Account-specific behavior comes from props (navItems, viewModeSwitcher, ...).
import React, { useContext, useEffect, useState } from "react";
import {
  X,
  Menu,
  Sun,
  Moon,
  Lock,
} from "lucide-react";

import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { useNavigation } from "../../constant/NavigationContext";
import { useModal } from "../../constant/ModalContext";
import { useLogout } from "../../hooks/useLogout";
import { useProfile } from "../../hooks/useProfile";
import AlertModal from "../../components/AlertModal";
import NotificationBell from "../../components/Notifications/NotificationBell";
import UnreadBadge from "../../components/UnreadBadge";

import logo from "../../Images/photos/icon.png";
import logo1 from "../../Images/photos/icon121.png";
import arabicLogoLight from "../../Images/Arabic-Logo-without-BG.png";
import arabicLogoDark from "../../Images/Arabic-Logo-without-BG-Dark.png";
import englishLogoLight from "../../Images/English Nexploy V2.png";
import englishLogoDark from "../../Images/English Nexploy white V2.png";

import { SidebarProvider } from "./SidebarContext";
import { sidebarTranslations } from "./translations";
import { sidebarColors, getSidebarStyles, getSidebarGlobalCss } from "./sidebarStyles";
import { useResponsiveBreakpoints } from "./hooks/useResponsiveBreakpoints";
import { useSidebarCollapse } from "./hooks/useSidebarCollapse";
import { useProfilePicture } from "./hooks/useProfilePicture";
import Tooltip from "./components/Tooltip";
import LogoutModal from "./components/LogoutModal";
import UserProfileDropdown from "./components/UserProfileDropdown";
import DateTimeDisplay from "./components/DateTimeDisplay";

// Preload the logos the moment the app bundle loads (module scope runs once,
// long before login) so the sidebar header renders them instantly from cache.
[logo, logo1, arabicLogoLight, arabicLogoDark, englishLogoLight, englishLogoDark].forEach(
  (src) => {
    const img = new Image();
    img.src = src;
  }
);

const SidebarWithTopbar = ({
  navItems,
  currentPage,
  handlePageChange,
  user,
  sidebarOpen,
  setSidebarOpen,
  children,
  viewModeSwitcher,
}) => {
  const { theme, setTheme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isRTL = language === "ar";

  // Ensure Arabic text renders correctly by forcing RTL direction
  // and using Arabic-capable fallback fonts when language is Arabic.
  useEffect(() => {
    if (isRTL) {
      try {
        document.documentElement.dir = "rtl";
        document.documentElement.lang = "ar";
        document.body.style.fontFamily =
          "'Cairo', 'Noto Naskh Arabic', 'Noto Sans Arabic', Tahoma, Arial, sans-serif";
        document.body.style.unicodeBidi = "embed";
      } catch (e) {
        // ignore DOM write errors in non-browser contexts
      }
    } else {
      try {
        document.documentElement.dir = "ltr";
        document.documentElement.lang = "en";
        document.body.style.fontFamily = "";
        document.body.style.unicodeBidi = "";
      } catch (e) {}
    }
  }, [isRTL]);

  const nav = useNavigation();
  const page =
    typeof handlePageChange === "function"
      ? currentPage
      : nav?.page ?? currentPage;
  const setPage =
    typeof handlePageChange === "function"
      ? handlePageChange
      : nav?.setPage ?? (() => {});
  const { showModal } = useModal();
  const { profile } = useProfile();

  const t = sidebarTranslations[language];

  // ===== STATE =====
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const userProfileRef = React.useRef(null);

  // ===== ALERT MODAL =====
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info'
  });
  const showAlert = (title, message, variant = 'info') => {
    setAlertModal({ open: true, title, message, variant });
  };
  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, open: false }));
  };

  // ===== SHARED HOOKS =====
  const { isMobile, isTablet, fontSizes, spacing } = useResponsiveBreakpoints();

  const {
    isCollapsed,
    setIsCollapsed,
    hoveredNavItem,
    sidebarRef,
    handleMouseEnter,
    handleMouseLeave,
    handleSidebarToggle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useSidebarCollapse({ isMobile, isRTL, sidebarOpen, setSidebarOpen });

  const {
    profilePic,
    isUploadingProfilePic,
    profilePicInputRef,
    handleProfilePicSelect,
    triggerProfilePicInput,
  } = useProfilePicture({ profile, language, t, showAlert });

  // ===== LOGOUT =====
  const { logout } = useLogout();

  const handleLogoutClick = () => {
    setShowUserProfile(false);
    setShowLogoutModal(true);
  };

  const completeLogout = async () => {
    setShowLogoutModal(false);
    await logout();
  };

  // ===== MOBILE DRAWER DISCIPLINE =====
  // The shells default sidebarOpen to true (desktop semantics). On mobile the
  // sidebar is a full-screen drawer, so it must start CLOSED and close again
  // after every navigation — never leave the user staring at the menu.
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ===== KEYBOARD (ESC closes open panels) =====
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        if (showUserProfile) {
          setShowUserProfile(false);
        } else if (sidebarOpen) {
          setSidebarOpen(false);
        }
      }
    };
    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [sidebarOpen, showUserProfile, setSidebarOpen]);

  // Close the user profile dropdown when clicking outside it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userProfileRef.current &&
        !userProfileRef.current.contains(event.target)
      ) {
        setShowUserProfile(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // ===== EVENT HANDLERS =====
  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  const toggleUserProfile = (event) => {
    event.stopPropagation();
    setShowUserProfile(!showUserProfile);
  };

  // ===== USER DATA =====
  const userData = {
    name:
      language === "ar"
        ? profile?.employeeArabicName ||
          profile?.employeeName ||
          " محمد عبدالمعطي"
        : profile?.employeeName || "Mohamed Abdelmoati",
    username: user?.username || "mohamed.abdelmoati",
    email: profile?.employeeEmail,
    phone: profile?.employeePhone,
    avatar: profilePic || "/default-avatar.png",
    status: "online",
    lastSeen: "2 minutes ago",
  };

  // ===== STYLES =====
  const c = sidebarColors[theme];

  const getStatusColor = (status) => {
    switch (status) {
      case "online":
        return c.statusOnline;
      case "offline":
        return c.statusOffline;
      case "away":
        return c.statusAway;
      case "busy":
        return c.statusBusy;
      default:
        return c.statusOffline;
    }
  };
  const styles = getSidebarStyles({
    c,
    theme,
    isRTL,
    isMobile,
    isTablet,
    isCollapsed,
    sidebarOpen,
    showUserProfile,
    fontSizes,
    spacing,
  });

  return (
    <div
      style={{ position: "relative", width: "100%", height: "100vh" }}
      className={`${theme} app-shell-root`}
    >
      {/* ===== GLOBAL STYLES ===== */}
      <style>
        {getSidebarGlobalCss({ c, theme, isMobile, isTablet, isCollapsed })}
      </style>

      {/* ===== LOGOUT MODAL ===== */}
      <LogoutModal
        show={showLogoutModal}
        theme={theme}
        isMobile={isMobile}
        isRTL={isRTL}
        onConfirm={completeLogout}
        onCancel={() => setShowLogoutModal(false)}
      />

      <div
        style={styles.wrapper}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* ===== MOBILE OVERLAY ===== */}
        <div style={styles.overlay} onClick={() => setSidebarOpen(false)} />

        {/* ===== SIDEBAR ===== */}
        <div
          ref={sidebarRef}
          style={styles.sidebar}
          className="slide-in-left app-sidebar"
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div style={{ ...styles.sidebarHeader, cursor: "pointer" }} onClick={handleSidebarToggle}>
            <div style={{ textDecoration: "none", flexShrink: 0 }}>
              <div
                style={{
                  ...styles.logo,
                }}
              >
                <img
                  src={theme === "dark" ? logo1 : logo}
                  alt="Logo"
                  style={{
                    width: "140%",
                    height: "140%",
                    objectFit: "cover",
                  }}
                />
              </div>
            </div>
            <div
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                height: "100%",
                opacity: isCollapsed && !isMobile ? 0 : 1,
                maxWidth: isCollapsed && !isMobile ? 0 : "200px",
                overflow: "hidden",
                transition: "opacity 0.4s ease, max-width 0.4s ease",
                whiteSpace: "nowrap",
                marginLeft: isRTL ? 0 : "-1.5rem",
                marginRight: isRTL ? "-1.5rem" : 0,
              }}
            >
              {language === "ar" ? (
                <img
                  src={theme === "dark" ? arabicLogoDark : arabicLogoLight}
                  alt="NexPloy Arabic"
                  style={{
                    height: "8rem",
                    width: "200%",
                    marginBottom: "-1.5rem",
                    marginTop: "-1.5rem",
                    marginLeft: "0.2rem",
                  }}
                />
              ) : (
                <img
                  src={theme === "dark" ? englishLogoDark : englishLogoLight}
                  alt="NexPloy English"
                  style={{
                    height: "4.5rem",
                    maxWidth: "200px",
                    width: "auto",
                    objectFit: "contain",
                    marginTop: "0.6rem",
                  }}
                />
              )}
            </div>
          </div>

          <nav style={styles.nav} className="nav">
            {navItems.map((item) => (
              <Tooltip
                key={item.page}
                text={isCollapsed && !isMobile ? item.text : ""}
                show={isCollapsed && !isMobile && hoveredNavItem === item.page}
              >
                <div
                  style={{
                    ...styles.navItem,
                    ...(page === item.page ? styles.navItemActive : {}),
                  }}
                  className="nav-item"
                  onClick={() => {
                    if (item.locked) {
                      const title = t.notice || "Notice";
                      const message = item.lockMessage || undefined; // let ModalContext default to localized featureNotAvailable
                      showModal({
                        title,
                        message,
                        options: {
                          requestAccessEndpoint: process.env.REACT_APP_BASE_URL
                            ? `${process.env.REACT_APP_BASE_URL}/api/request-module-access`
                            : null,
                          module: item.page,
                        },
                      });
                      return;
                    }
                    setPage(item.page);
                    // Selecting a destination dismisses the mobile drawer.
                    if (isMobile) setSidebarOpen(false);
                  }}
                >
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <item.icon size={20} />
                    {item.badge !== undefined && (
                      <div style={{ position: 'absolute', top: '-8px', right: '-8px' }}>
                        <UnreadBadge
                          count={item.badge}
                          size={16}
                          background={item.badgeColor || '#2563eb'}
                          color="#ffffff"
                        />
                      </div>
                    )}
                  </div>
                  {!isCollapsed && (
                    <span style={styles.navItemText}>{item.text}</span>
                  )}
                  {!isCollapsed && item.accessDenied && (
                    <Lock
                      size={12}
                      style={{ opacity: 0.6, flexShrink: 0, marginInlineStart: "auto" }}
                    />
                  )}
                </div>
              </Tooltip>
            ))}
          </nav>
        </div>

        {/* Drawer-edge close button — only while the mobile drawer is open.
            (The OPEN control lives in the topbar as a proper hamburger; the
            old always-rendered floating circle sat half outside the viewport
            when closed.) */}
        {isMobile && sidebarOpen && (
          <button
            onClick={handleSidebarToggle}
            style={{
              position: "fixed",
              top: "4.75rem",
              [isRTL ? "right" : "left"]: "min(280px, 80vw)",
              transform: isRTL ? "translateX(50%)" : "translateX(-50%)",
              width: "2.5rem",
              height: "2.5rem",
              borderRadius: "50%",
              background: c.topbarBg,
              border: `1px solid ${c.sidebarBorder}`,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 1050,
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            title={t.closeSidebar}
          >
            <X size={16} color={c.text} />
          </button>
        )}

        {/* ===== MAIN CONTENT ===== */}
        <div style={styles.main} className="app-shell-main">
          {/* ===== TOPBAR ===== */}
          <div style={styles.topbar}>
            <div style={styles.topbarLeft}>
              {/* Mobile: hamburger lives IN the topbar (the drawer's open
                  control) — a fixed floating button was clipped at the edge. */}
              {isMobile && !sidebarOpen && (
                <button
                  style={styles.menuBtn}
                  className="menu-btn"
                  onClick={handleSidebarToggle}
                  title={t.openSidebar}
                >
                  <Menu size={20} color={c.text} />
                </button>
              )}
              {/* ===== DATE TIME DISPLAY ===== */}
              <DateTimeDisplay
                theme={theme}
                language={language}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            </div>

            <div style={styles.topbarRight}>
              {isRTL ? (
                <>
                  {/* RTL order: User ← Bell ← Theme ← Switcher */}
                  <UserProfileDropdown
                    styles={styles}
                    t={t}
                    language={language}
                    isMobile={isMobile}
                    userData={userData}
                    profilePic={profilePic}
                    showUserProfile={showUserProfile}
                    toggleUserProfile={toggleUserProfile}
                    userProfileRef={userProfileRef}
                    getStatusColor={getStatusColor}
                    isUploadingProfilePic={isUploadingProfilePic}
                    profilePicInputRef={profilePicInputRef}
                    handleProfilePicSelect={handleProfilePicSelect}
                    triggerProfilePicInput={triggerProfilePicInput}
                    onLogoutClick={handleLogoutClick}
                  />

                  <NotificationBell isMobile={isMobile} />

                  <button
                    style={styles.themeToggle}
                    className="theme-toggle"
                    onClick={toggleTheme}
                    title={theme === "light" ? t.switchToDark : t.switchToLight}
                  >
                    {theme === "light" ? (
                      <Moon size={isMobile ? 18 : 22} />
                    ) : (
                      <Sun size={isMobile ? 18 : 22} />
                    )}
                  </button>

                  {viewModeSwitcher}
                </>
              ) : (
                <>
                  {/* LTR order: Switcher → Theme → Bell → User */}
                  {viewModeSwitcher}

                  <button
                    style={styles.themeToggle}
                    className="theme-toggle"
                    onClick={toggleTheme}
                    title={theme === "light" ? t.switchToDark : t.switchToLight}
                  >
                    {theme === "light" ? (
                      <Moon size={isMobile ? 18 : 22} />
                    ) : (
                      <Sun size={isMobile ? 18 : 22} />
                    )}
                  </button>

                  <NotificationBell isMobile={isMobile} />

                  <UserProfileDropdown
                    styles={styles}
                    t={t}
                    language={language}
                    isMobile={isMobile}
                    userData={userData}
                    profilePic={profilePic}
                    showUserProfile={showUserProfile}
                    toggleUserProfile={toggleUserProfile}
                    userProfileRef={userProfileRef}
                    getStatusColor={getStatusColor}
                    isUploadingProfilePic={isUploadingProfilePic}
                    profilePicInputRef={profilePicInputRef}
                    handleProfilePicSelect={handleProfilePicSelect}
                    triggerProfilePicInput={triggerProfilePicInput}
                    onLogoutClick={handleLogoutClick}
                  />
                </>
              )}
            </div>
          </div>

          {/* ===== CONTENT ===== */}
          <div style={styles.content} className="fade-in content">
            <SidebarProvider value={{ isCollapsed, setIsCollapsed }}>
              {React.cloneElement(children, {
                isCollapsed,
                isMobile,
                isTablet,
                isRTL,
              })}
            </SidebarProvider>
          </div>
        </div>
      </div>

      {/* ===== ALERT MODAL ===== */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={closeAlert}
        theme={theme === 'dark' ? 'dark' : 'light'}
        language={language}
      />
    </div>
  );
};

export default SidebarWithTopbar;
